import Image from "next/image";
import { PayloadLink } from "@/components/shared/PayloadLink";
import type { NavItemConfig } from "@/lib/navigation-utils";
import { getCardColorScheme } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { getImageData } from "@/lib/media-utils";

type ServicesLayoutColumns = NonNullable<NavItemConfig["servicesLayout"]>["columns"];

interface MegaMenuServicesLayoutProps {
  columns?: ServicesLayoutColumns | null;
  onNavigate?: () => void;
}

const lightColumnColors = new Set(["white", "gray"]);

export function MegaMenuServicesLayout({ columns, onNavigate }: MegaMenuServicesLayoutProps) {
  if (!columns || columns.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        No columns configured. Please add services layout columns in the CMS.
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {columns.map((column, columnIndex) => {
          const columnColour = column.columnColour || "brand";
          const colorScheme = getCardColorScheme(columnColour);
          const isDark = colorScheme.text.includes("text-white");
          const needsBorder = lightColumnColors.has(columnColour);
          const containerClass = cn(
            "rounded-lg shadow-sm h-full overflow-hidden",
            colorScheme.background,
            colorScheme.text,
            needsBorder && "border border-gray-200"
          );
          const { url: imageUrl, alt: imageAlt, objectPosition } = getImageData(
            column.image,
            column.heading || "Services column"
          );

          return (
            <div
              key={column.id || columnIndex}
              className={containerClass}
            >
              <div className="flex h-full flex-col">
                <div className="flex min-w-0 flex-1 flex-col gap-3 p-5">
                  <div>
                    <h3
                      className={cn(
                        "font-subheading text-lg",
                        isDark ? "text-white" : "text-gray-900"
                      )}
                    >
                      {column.heading}
                    </h3>
                    {column.description && (
                      <p
                        className={cn(
                          "text-sm mt-1",
                          isDark ? "text-white/80" : "text-gray-600"
                        )}
                      >
                        {column.description}
                      </p>
                    )}
                  </div>

                  {column.subheading && (
                    <p
                      className={cn(
                        "text-xs font-semibold uppercase tracking-wide",
                        isDark ? "text-white/70" : "text-gray-500"
                      )}
                    >
                      {column.subheading}
                    </p>
                  )}

                  {column.items && column.items.length > 0 && (
                    <ul className="flex flex-col gap-2">
                      {column.items.map((item, itemIndex) => (
                        <li key={item.id || itemIndex}>
                          <PayloadLink
                            type={item.type}
                            page={item.page}
                            url={item.url}
                            newTab={item.newTab}
                            onClick={onNavigate}
                            className={cn(
                              "text-sm transition-colors",
                              isDark
                                ? "text-white/90 hover:text-white"
                                : "text-gray-700 hover:text-brand"
                            )}
                          >
                            {item.label}
                          </PayloadLink>
                        </li>
                      ))}
                    </ul>
                  )}

                  {column.link?.label && (
                    <div className="mt-auto pt-3">
                      <PayloadLink
                        type={column.link.type}
                        page={column.link.page}
                        url={column.link.url}
                        newTab={column.link.newTab}
                        onClick={onNavigate}
                        className={cn(
                          "text-sm font-semibold transition-colors hover:underline",
                          isDark ? "text-white hover:text-white/90" : "text-brand hover:text-brand-dark"
                        )}
                      >
                        {column.link.label}
                      </PayloadLink>
                    </div>
                  )}
                </div>
                {imageUrl && (
                  <div className="relative w-full h-28 sm:h-32 mt-4">
                    <Image
                      src={imageUrl}
                      alt={imageAlt}
                      fill
                      className="object-cover"
                      style={{ objectPosition }}
                      sizes="(max-width: 1024px) 100vw, 25vw"
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
